import React, { useCallback, useState } from 'react';
import {
  View,
  Image,
  ScrollView,
  StatusBar,
  ActivityIndicator,
  StyleSheet,
  RefreshControl,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { InboxConversation, formatConversationDate } from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { useGetConversationsQuery } from '@/store/api';
import {
  GradientBackground,
  PageHeader,
  GlassCard,
  ProfileNavTabs,
} from '@/components/residents/redesign';

const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;
const EmptySoldIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_SOLD;

type MarketplaceInboxNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceInbox'>;

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;

export const MarketplaceInboxScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopInboxView />;
  return <MobileInboxView />;
};

function useInboxData() {
  const navigation = useNavigation<MarketplaceInboxNavigationProp>();

  const {
    data: conversations = [],
    isLoading,
    refetch,
  } = useGetConversationsQuery();

  useFocusEffect(useCallback(() => { refetch(); }, [refetch]));

  const handleConversationPress = useCallback((conversation: InboxConversation) => {
    navigation.navigate('InboxConversation', { conversationId: conversation.id });
  }, [navigation]);

  return { conversations, isLoading, refetch, handleConversationPress };
}

const InboxContent: React.FC<{
  conversations: InboxConversation[];
  isLoading: boolean;
  onConversationPress: (c: InboxConversation) => void;
}> = ({ conversations, isLoading, onConversationPress }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.contentGap}>
      <ProfileNavTabs activeOption="inbox" />

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      ) : conversations.length > 0 ? (
        <GlassCard borderRadius={16} padding={0}>
          {conversations.map((conversation, index) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              onPress={() => onConversationPress(conversation)}
              showDivider={index < conversations.length - 1}
            />
          ))}
        </GlassCard>
      ) : (
        <View style={styles.emptyContainer}>
          <EmptySoldIcon width={60} height={60} color={colors.text.muted} />
          <Text style={[styles.emptyTitle, { color: colors.text.secondary }]}>No conversations yet</Text>
          <Text style={[styles.emptySubtitle, { color: colors.text.dim }]}>Start a conversation by contacting a seller on a listing</Text>
        </View>
      )}
    </View>
  );
};

const ConversationRow: React.FC<{
  conversation: InboxConversation;
  onPress: () => void;
  showDivider: boolean;
}> = ({ conversation, onPress, showDivider }) => {
  const { colors } = useThemeColors();
  const { listing, otherUser, lastMessage, isUnread } = conversation;
  const formattedDate = formatConversationDate(lastMessage.timestamp);

  return (
    <GlassCard
      interactive
      onPress={onPress}
      borderRadius={0}
      padding={0}
      style={[styles.convRow, isUnread && { backgroundColor: colors.glass.buttonBg }]}
    >
      <View style={[styles.convInner, showDivider && { borderBottomWidth: 1, borderBottomColor: colors.separator }]}>
        {listing.image ? (
          <Image source={{ uri: listing.image }} style={[styles.convThumb, { backgroundColor: colors.card.bg }]} resizeMode="cover" />
        ) : (
          <View style={[styles.convThumb, styles.convThumbPlaceholder, { backgroundColor: colors.card.bg }]}>
            <PropertyPlaceholder width={28} height={28} />
          </View>
        )}

        <View style={styles.convContent}>
          <View style={styles.convTopRow}>
            <Text style={[styles.convTitle, { color: colors.text.primary }]} numberOfLines={2}>{listing.title}</Text>
            <Text style={[styles.convDate, { color: colors.text.dimmer }]}>{formattedDate}</Text>
          </View>
          <Text style={[styles.convMessage, { color: colors.text.muted }]} numberOfLines={1}>{lastMessage.text}</Text>
          <Text style={[styles.convUserName, { color: colors.text.dimmer }]} numberOfLines={1}>{otherUser.name}</Text>
        </View>

        {isUnread && <View style={styles.convUnreadDot} />}
      </View>
    </GlassCard>
  );
};

const DesktopInboxView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const { conversations, isLoading, refetch, handleConversationPress } = useInboxData();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => { setRefreshing(true); await refetch(); setRefreshing(false); }, [refetch]);
  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <ScrollView
      style={styles.desktopContainer}
      contentContainerStyle={styles.desktopScrollContent}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />}
    >
      <View style={styles.desktopCentered}>
        <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
        <PageHeader title="Inbox" />
        <InboxContent conversations={conversations} isLoading={isLoading} onConversationPress={handleConversationPress} />
      </View>
    </ScrollView>
  );
};

const MobileInboxView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const { conversations, isLoading, refetch, handleConversationPress } = useInboxData();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => { setRefreshing(true); await refetch(); setRefreshing(false); }, [refetch]);
  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen screenName="Marketplace" darkBackground headerVariant="gradient">
        <ScrollView
          style={styles.mobileScroll}
          contentContainerStyle={styles.mobileScrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />}
        >
          <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
          <PageHeader title="Inbox" />
          <InboxContent conversations={conversations} isLoading={isLoading} onConversationPress={handleConversationPress} />
        </ScrollView>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  desktopContainer: { flex: 1, backgroundColor: 'transparent' },
  desktopScrollContent: { padding: HP, paddingBottom: 60, alignItems: 'center' },
  desktopCentered: { width: '100%', maxWidth: MAX_CONTENT_WIDTH, gap: 8 },
  mobileScroll: { flex: 1 },
  mobileScrollContent: { paddingHorizontal: HP, paddingBottom: 40, gap: 8 },
  contentGap: { gap: 12 },
  loadingContainer: { paddingVertical: 60, alignItems: 'center', justifyContent: 'center' },
  emptyContainer: { alignItems: 'center', gap: 12, paddingVertical: 40 },
  emptyTitle: { ...REDESIGN_TYPOGRAPHY.cardTitleLarge, textAlign: 'center' },
  emptySubtitle: { ...REDESIGN_TYPOGRAPHY.body, textAlign: 'center', maxWidth: 280 },
  convRow: { backgroundColor: 'transparent' },
  convInner: { flexDirection: 'row', alignItems: 'flex-start', paddingHorizontal: 20, paddingVertical: 14, gap: 14 },
  convThumb: { width: 46, height: 46, borderRadius: 23 },
  convThumbPlaceholder: { alignItems: 'center', justifyContent: 'center' },
  convContent: { flex: 1, gap: 3 },
  convTopRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  convTitle: { flex: 1, ...REDESIGN_TYPOGRAPHY.cardTitle },
  convDate: { ...REDESIGN_TYPOGRAPHY.timestamp, textAlign: 'right' },
  convMessage: { ...REDESIGN_TYPOGRAPHY.body },
  convUserName: { ...REDESIGN_TYPOGRAPHY.timestamp },
  convUnreadDot: { position: 'absolute', right: 20, top: '80%', width: 8, height: 8, borderRadius: 4, backgroundColor: PRIMITIVE_COLORS.warningOrange, transform: [{ translateY: -4 }] },
});
